import { useState } from "react";
import { Plus, Leaf, Star } from "lucide-react";
import { useCart } from "../lib/cart";
import { absUrl } from "../lib/api";
import ItemOptionsDialog from "./ItemOptionsDialog";
import { toast } from "sonner";

export const slugOf = (name) => name.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

export function DishImage({ item, className = "" }) {
  return (
    <div className={`relative bg-[#F3EDE4] overflow-hidden ${className}`}>
      {item.image_url ? (
        <img src={absUrl(item.image_url)} alt={item.name} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
      ) : (
        <div className="w-full h-full grid place-items-center text-gray-300 text-4xl font-display">M</div>
      )}
    </div>
  );
}

export default function MenuCard({ item, extras = [], unavailableLabel = "Not available" }) {
  const { add, qtyFor } = useCart();
  const [open, setOpen] = useState(false);
  const qty = qtyFor(item.id);
  const slug = slugOf(item.name);
  const hasOptions = (item.sides || []).length > 0 || (item.extra_ids || []).some((id) => extras.some((e) => e.id === id));

  const onAdd = () => {
    if (hasOptions) { setOpen(true); return; }
    add(item);
    toast.success(`${item.name} added`);
  };

  const blocked = !!item.sold_out;

  return (
    <div data-testid={`menu-card-${slug}`}
      className={`group bg-white rounded-2xl border border-[#EFE7DA] overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 flex flex-col ${blocked ? "opacity-75" : ""}`}>
      <div className="relative">
        <DishImage item={item} className="aspect-[4/3]" />
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {item.special && <span className="bg-[#111111] text-[#FF8A3D] text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1"><Star size={10} /> Today's special</span>}
          {item.badge && <span className="bg-white/95 text-[#CC1A00] text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">{item.badge}</span>}
        </div>
        {blocked && (
          <div className="absolute inset-0 bg-black/45 grid place-items-center p-2" data-testid={`unavailable-${slug}`}>
            <span className="bg-white text-[#111] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full text-center max-w-full break-words">{unavailableLabel}</span>
          </div>
        )}
      </div>

      <div className="p-3 sm:p-4 flex-1 flex flex-col">
        <div className="font-display font-semibold text-gray-900 leading-tight line-clamp-2 text-sm sm:text-base">{item.name}</div>
        {item.vegetarian && (
          <div data-testid={`veg-${slug}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#2F7A3A]"><Leaf size={11} /> Vegetarian</div>
        )}
        <div className="text-xs text-gray-500 line-clamp-2 mt-1 flex-1">{item.description}</div>

        <div className="mt-3 flex items-center justify-between gap-1">
          <div className="text-[#CC1A00] font-bold whitespace-nowrap text-sm sm:text-base">KSh {item.price}</div>
          <button data-testid={`add-${slug}`} onClick={onAdd} disabled={blocked}
            className="relative mlobox-gradient text-white rounded-full px-3 py-1.5 text-sm font-semibold flex items-center gap-1 shadow hover:brightness-110 disabled:grayscale disabled:cursor-not-allowed">
            <Plus size={14} /> Add
            {qty > 0 && <span data-testid={`qty-${slug}`} className="absolute -top-2 -right-2 bg-[#111111] text-white text-[10px] h-5 min-w-5 px-1 rounded-full grid place-items-center">{qty}</span>}
          </button>
        </div>
      </div>
      {hasOptions && <ItemOptionsDialog item={item} extras={extras} open={open} onOpenChange={setOpen} />}
    </div>
  );
}
